import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  AlignHorizontalJustifyStart, Copy, Eye, EyeOff, Film, Lock, Magnet, Maximize2, Mic, Music, Plus, Scissors, Trash2,
  Type, Unlock, Volume2, VolumeX, X, ZoomIn, ZoomOut,
} from 'lucide-react'
import { resolveTrack, useEditor } from '../store/editor'
import type { Asset, Clip, Project, Track } from '../types'
import { assetOf, clipEnd, hasTimedSource, trackKindFor } from '../lib/model'
import { getWaveform, mediaStripUrl } from '../lib/api'
import { uploadFiles } from '../lib/actions'

const RULER_H = 26
const SNAP_PX = 8

function laneHeight(p: Project, t: Track): number {
  if (t.kind === 'audio') return 50
  const onlyText = p.clips.some((c) => c.trackId === t.id) && p.clips.every((c) => c.trackId !== t.id || c.kind === 'text' || c.kind === 'subtitle')
  return onlyText ? 36 : 58
}

function isMain(p: Project, t: Track): boolean {
  const vids = p.tracks.filter((x) => x.kind === 'video')
  return vids[vids.length - 1]?.id === t.id
}

export default function Timeline() {
  const project = useEditor((s) => s.project)
  const pps = useEditor((s) => s.pps)
  const selection = useEditor((s) => s.selection)
  const scrollRef = useRef<HTMLDivElement>(null)
  const headsRef = useRef<HTMLDivElement>(null)
  const [viewW, setViewW] = useState(800)
  const [snapAt, setSnapAt] = useState<number | null>(null)
  const [dropLane, setDropLane] = useState<string | null>(null)
  const [marquee, setMarquee] = useState<{ x: number; y: number; w: number; h: number } | null>(null)

  const dur = project.durationSec
  const contentW = Math.max(viewW, (dur + Math.max(10, dur * 0.3)) * pps)
  const lanes = project.tracks.map((t) => ({ t, h: laneHeight(project, t) }))
  let acc = RULER_H
  const laneTop = new Map<string, number>()
  for (const l of lanes) { laneTop.set(l.t.id, acc); acc += l.h }
  const contentH = acc + 40

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setViewW(el.clientWidth))
    ro.observe(el)
    setViewW(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  // keep playhead in view while playing
  useEffect(() => useEditor.subscribe((s) => {
    const el = scrollRef.current
    if (!el || !s.playing) return
    const x = s.playhead * s.pps
    if (x < el.scrollLeft || x > el.scrollLeft + el.clientWidth - 40) el.scrollLeft = Math.max(0, x - el.clientWidth * 0.1)
  }), [])

  // ctrl/cmd + wheel zoom anchored at the pointer
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return
      e.preventDefault()
      const s = useEditor.getState()
      const rect = el.getBoundingClientRect()
      const px = e.clientX - rect.left
      const tAt = (el.scrollLeft + px) / s.pps
      const next = Math.min(600, Math.max(4, s.pps * (e.deltaY < 0 ? 1.15 : 1 / 1.15)))
      s.setPps(next)
      requestAnimationFrame(() => { el.scrollLeft = Math.max(0, tAt * next - px) })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  const timeAtClientX = useCallback((clientX: number) => {
    const el = scrollRef.current!
    const rect = el.getBoundingClientRect()
    return Math.max(0, (clientX - rect.left + el.scrollLeft) / useEditor.getState().pps)
  }, [])

  const laneAtClientY = useCallback((clientY: number): Track | null => {
    const el = scrollRef.current!
    const rect = el.getBoundingClientRect()
    const y = clientY - rect.top + el.scrollTop
    const p = useEditor.getState().project
    for (const t of p.tracks) {
      const top = laneTop.get(t.id) ?? 0
      if (y >= top && y < top + laneHeight(p, t)) return t
    }
    return null
  }, [laneTop])

  // ---------------- scrubbing ----------------
  const scrub = (e: React.PointerEvent) => {
    e.preventDefault()
    const s = useEditor.getState()
    s.set({ playing: false })
    s.setPlayhead(timeAtClientX(e.clientX))
    const move = (ev: PointerEvent) => useEditor.getState().setPlayhead(Math.min(timeAtClientX(ev.clientX), useEditor.getState().project.durationSec))
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  // ---------------- snapping ----------------
  const snapTargets = (exclude: Set<string>): number[] => {
    const s = useEditor.getState()
    const out = [0, s.playhead]
    for (const c of s.project.clips) {
      if (exclude.has(c.id)) continue
      out.push(c.startSec, clipEnd(c))
    }
    return out
  }
  const snap = (times: number[], targets: number[]): { delta: number; at: number | null } => {
    const s = useEditor.getState()
    if (!s.snapping) return { delta: 0, at: null }
    const thr = SNAP_PX / s.pps
    let best = { delta: 0, at: null as number | null, d: thr }
    for (const t of times) for (const g of targets) {
      const d = Math.abs(g - t)
      if (d < best.d) best = { delta: g - t, at: g, d }
    }
    return { delta: best.delta, at: best.at }
  }

  // ---------------- clip move ----------------
  const onClipDown = (e: React.PointerEvent, clip: Clip) => {
    if (e.button !== 0) return
    e.stopPropagation()
    const s = useEditor.getState()
    const track = s.project.tracks.find((t) => t.id === clip.trackId)
    if (e.shiftKey || e.ctrlKey || e.metaKey) { s.toggleSelect(clip.id); return }
    if (!s.selection.includes(clip.id)) s.select([clip.id])
    if (track?.locked) return
    const ids = new Set(useEditor.getState().selection)
    const lockedTracks = new Set(s.project.tracks.filter((t) => t.locked).map((t) => t.id))
    const moving = s.project.clips.filter((c) => ids.has(c.id) && !lockedTracks.has(c.trackId))
    const orig = new Map(moving.map((c) => [c.id, { start: c.startSec, track: c.trackId }]))
    const x0 = e.clientX
    const y0 = e.clientY
    let started = false
    const single = moving.length === 1
    const targets = snapTargets(ids)
    const minStart = Math.min(...moving.map((c) => c.startSec))

    const move = (ev: PointerEvent) => {
      if (!started) {
        if (Math.abs(ev.clientX - x0) < 3 && Math.abs(ev.clientY - y0) < 3) return
        started = true
        useEditor.getState().beginGesture()
      }
      const st = useEditor.getState()
      let delta = (ev.clientX - x0) / st.pps
      const g = orig.get(clip.id)!
      const sn = snap([g.start + delta, g.start + delta + clip.durationSec], targets)
      delta += sn.delta
      setSnapAt(sn.at)
      if (minStart + delta < 0) delta = -minStart
      let targetTrack: string | null = null
      if (single) {
        const lane = laneAtClientY(ev.clientY)
        if (lane && lane.kind === trackKindFor(clip.kind) && !lane.locked) targetTrack = lane.id
      }
      st.mutate((p) => {
        for (const c of p.clips) {
          const o = orig.get(c.id)
          if (!o) continue
          c.startSec = Math.max(0, o.start + delta)
          if (targetTrack) c.trackId = targetTrack
        }
      }, { history: false })
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setSnapAt(null)
      if (!started) return
      // resolve overlaps: a clip landing on an occupied spot moves to a free/new track
      useEditor.getState().mutate((p) => {
        const movedIds = new Set(orig.keys())
        for (const c of p.clips) {
          if (!movedIds.has(c.id)) continue
          const others = new Set([...movedIds].filter((x) => x !== c.id))
          const clash = p.clips.some((o) => o.trackId === c.trackId && o.id !== c.id && !others.has(o.id) &&
            o.startSec < clipEnd(c) - 1e-3 && clipEnd(o) > c.startSec + 1e-3)
          if (clash) c.trackId = resolveTrack(p, trackKindFor(c.kind), c.startSec, c.durationSec, c.trackId, new Set([c.id]))
        }
      }, { history: false })
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  // ---------------- trim ----------------
  const onTrimDown = (e: React.PointerEvent, clip: Clip, side: 'l' | 'r') => {
    e.stopPropagation()
    e.preventDefault()
    const s = useEditor.getState()
    if (s.project.tracks.find((t) => t.id === clip.trackId)?.locked) return
    s.select([clip.id])
    s.beginGesture()
    const p0 = s.project
    const c0 = p0.clips.find((c) => c.id === clip.id)!
    const asset = assetOf(p0, c0)
    const timed = hasTimedSource(c0)
    const sameTrack = p0.clips.filter((c) => c.trackId === c0.trackId && c.id !== c0.id)
    const prevEnd = sameTrack.filter((c) => clipEnd(c) <= c0.startSec + 1e-3).reduce((m, c) => Math.max(m, clipEnd(c)), 0)
    const nextStart = sameTrack.filter((c) => c.startSec >= clipEnd(c0) - 1e-3).reduce((m, c) => Math.min(m, c.startSec), Infinity)
    const end0 = clipEnd(c0)
    const targets = snapTargets(new Set([c0.id]))
    const x0 = e.clientX

    const move = (ev: PointerEvent) => {
      const st = useEditor.getState()
      const d = (ev.clientX - x0) / st.pps
      st.mutate((p) => {
        const c = p.clips.find((x) => x.id === c0.id)
        if (!c) return
        if (side === 'l') {
          let ns = c0.startSec + d
          const sn = snap([ns], targets); ns += sn.delta; setSnapAt(sn.at)
          const minS = Math.max(prevEnd, timed ? c0.startSec - c0.sourceInSec / c0.speed : 0, 0)
          ns = Math.max(minS, Math.min(end0 - 0.1, ns))
          c.startSec = ns
          c.durationSec = end0 - ns
          if (timed) c.sourceInSec = Math.max(0, c0.sourceInSec + (ns - c0.startSec) * c0.speed)
          else { c.sourceInSec = 0; c.sourceOutSec = c.durationSec }
        } else {
          let ne = end0 + d
          const sn = snap([ne], targets); ne += sn.delta; setSnapAt(sn.at)
          const maxE = Math.min(nextStart, timed && asset ? c0.startSec + (asset.info.durationSec - c0.sourceInSec) / c0.speed : Infinity)
          ne = Math.max(c0.startSec + 0.1, Math.min(maxE, ne))
          c.durationSec = ne - c0.startSec
          if (timed) c.sourceOutSec = c0.sourceInSec + c.durationSec * c0.speed
          else { c.sourceInSec = 0; c.sourceOutSec = c.durationSec }
        }
        c.audio.fadeInSec = Math.min(c.audio.fadeInSec, c.durationSec / 2)
        c.audio.fadeOutSec = Math.min(c.audio.fadeOutSec, c.durationSec / 2)
      }, { history: false })
    }
    const up = () => {
      setSnapAt(null)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  // ---------------- empty area: marquee / seek ----------------
  const onBackgroundDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    const el = scrollRef.current!
    const rect = el.getBoundingClientRect()
    const sx = e.clientX - rect.left + el.scrollLeft
    const sy = e.clientY - rect.top + el.scrollTop
    if (sy < RULER_H) return
    const additive = e.shiftKey || e.ctrlKey || e.metaKey
    const base = additive ? useEditor.getState().selection : []
    if (!additive) useEditor.getState().select([])
    let moved = false
    const move = (ev: PointerEvent) => {
      const x = ev.clientX - rect.left + el.scrollLeft
      const y = ev.clientY - rect.top + el.scrollTop
      if (!moved && Math.hypot(x - sx, y - sy) < 4) return
      moved = true
      const m = { x: Math.min(sx, x), y: Math.min(sy, y), w: Math.abs(x - sx), h: Math.abs(y - sy) }
      setMarquee(m)
      const st = useEditor.getState()
      const p = st.project
      const t0 = m.x / st.pps
      const t1 = (m.x + m.w) / st.pps
      const hit = p.clips.filter((c) => {
        const top = laneTop.get(c.trackId) ?? 0
        const tr = p.tracks.find((t) => t.id === c.trackId)!
        const h = laneHeight(p, tr)
        return c.startSec < t1 && clipEnd(c) > t0 && top < m.y + m.h && top + h > m.y
      }).map((c) => c.id)
      st.select([...new Set([...base, ...hit])])
    }
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setMarquee(null)
      if (!moved) {
        const s = useEditor.getState()
        s.set({ playing: false })
        s.setPlayhead(Math.min(timeAtClientX(ev.clientX), s.project.durationSec))
      }
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  // ---------------- drag & drop from library / desktop ----------------
  const onDragOver = (e: React.DragEvent) => {
    const types = e.dataTransfer.types
    if (!types.includes('application/x-vaideo-asset') && !types.includes('Files')) return
    e.preventDefault()
    const lane = laneAtClientY(e.clientY)
    setDropLane(lane?.id ?? null)
  }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDropLane(null)
    const t = timeAtClientX(e.clientX)
    const lane = laneAtClientY(e.clientY)
    const assetId = e.dataTransfer.getData('application/x-vaideo-asset')
    const st = useEditor.getState()
    if (assetId) {
      const a = st.project.assets.find((x) => x.id === assetId)
      if (!a) return
      const kind = trackKindFor(a.info.kind)
      st.addAssetClip(assetId, { startSec: t, trackId: lane && lane.kind === kind ? lane.id : undefined })
    } else if (e.dataTransfer.files.length) {
      uploadFiles(Array.from(e.dataTransfer.files), { startSec: t, trackId: lane?.id })
    }
  }

  return (
    <>
      <Toolbar viewW={viewW} />
      <div className="tl-body">
        <div className="tl-heads" ref={headsRef}>
          <div style={{ height: RULER_H, borderBottom: '1px solid var(--border)' }} />
          {lanes.map(({ t, h }) => <TrackHead key={t.id} track={t} height={h} main={isMain(project, t)} />)}
        </div>
        <div className="tl-scroll" ref={scrollRef} onScroll={(e) => { if (headsRef.current) headsRef.current.scrollTop = (e.target as HTMLElement).scrollTop }}
          onDragOver={onDragOver} onDragLeave={() => setDropLane(null)} onDrop={onDrop}>
          <div className="tl-content" style={{ width: contentW, height: contentH }} onPointerDown={onBackgroundDown}>
            <Ruler width={contentW} pps={pps} onPointerDown={scrub} />
            {lanes.map(({ t, h }) => (
              <div key={t.id} className={`lane ${isMain(project, t) ? 'main' : ''} ${dropLane === t.id ? 'drop' : ''}`} style={{ height: h }}>
                {project.clips.filter((c) => c.trackId === t.id).map((c) => (
                  <ClipView key={c.id} clip={c} pps={pps} height={h} selected={selection.includes(c.id)} locked={t.locked}
                    asset={assetOf(project, c)} onDown={onClipDown} onTrim={onTrimDown} />
                ))}
              </div>
            ))}
            {!project.clips.length && <div className="empty-tl">Arrastra aquí vídeos, audio o imágenes desde la biblioteca</div>}
            {snapAt !== null && <div className="snapline" style={{ left: snapAt * pps }} />}
            {marquee && <div className="marquee" style={{ left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h }} />}
            <Playhead height={contentH} onKnobDown={scrub} />
          </div>
        </div>
      </div>
    </>
  )
}

function Toolbar({ viewW }: { viewW: number }) {
  const hasSel = useEditor((s) => s.selection.length > 0)
  const snapping = useEditor((s) => s.snapping)
  const pps = useEditor((s) => s.pps)
  const s = useEditor.getState
  const zoomVal = Math.log(pps / 4) / Math.log(150) // 4..600 → 0..1
  return (
    <div className="tl-toolbar">
      <button className="btn ghost sm" title="Dividir en el cursor (S)" onClick={() => s().splitAtPlayhead()}><Scissors size={15} />Dividir</button>
      <button className="btn ghost icon sm" title="Eliminar (Supr)" disabled={!hasSel} onClick={() => s().deleteSelected()}><Trash2 size={15} /></button>
      <button className="btn ghost icon sm" title="Duplicar (Ctrl+D)" disabled={!hasSel} onClick={() => s().duplicateSelected()}><Copy size={15} /></button>
      <div className="sep" />
      <button className="btn ghost sm" title="Quitar los huecos entre clips" onClick={() => s().closeGaps()}><AlignHorizontalJustifyStart size={15} />Cerrar huecos</button>
      <button className="btn ghost sm" title="Añadir capa de vídeo" onClick={() => s().addTrack('video')}><Plus size={14} /><Film size={14} /></button>
      <button className="btn ghost sm" title="Añadir pista de audio" onClick={() => s().addTrack('audio')}><Plus size={14} /><Music size={14} /></button>
      <button className="btn ghost sm" title="Añadir texto" onClick={() => s().addText({ text: 'Texto', fontSize: 96 })}><Plus size={14} /><Type size={14} /></button>
      <div className="sep" />
      <button className="btn ghost sm" title="Generar o cambiar la voz con IA a partir de un texto" onClick={() => s().set({ dialog: 'voice' })}><Mic size={15} />Voz IA</button>
      <div className="spacer" />
      <button className={`btn ghost icon sm ${snapping ? 'active' : ''}`} title="Imán: ajustar a bordes y cursor" onClick={() => s().set({ snapping: !snapping })}><Magnet size={15} /></button>
      <div className="sep" />
      <button className="btn ghost icon sm" title="Alejar (-)" onClick={() => s().setPps(pps / 1.25)}><ZoomOut size={15} /></button>
      <input type="range" min={0} max={1} step={0.001} value={zoomVal} style={{ width: 120 }}
        onChange={(e) => s().setPps(4 * Math.pow(150, Number(e.target.value)))} />
      <button className="btn ghost icon sm" title="Acercar (+)" onClick={() => s().setPps(pps * 1.25)}><ZoomIn size={15} /></button>
      <button className="btn ghost icon sm" title="Ajustar a la ventana" onClick={() => s().setPps((viewW - 60) / Math.max(5, s().project.durationSec))}><Maximize2 size={15} /></button>
    </div>
  )
}

function TrackHead({ track, height, main }: { track: Track; height: number; main: boolean }) {
  const s = useEditor.getState
  const toggle = (k: 'muted' | 'locked' | 'visible') => s().mutate((p) => {
    const t = p.tracks.find((x) => x.id === track.id)
    if (t) t[k] = !t[k]
  })
  return (
    <div className={`tl-head ${main ? 'main' : ''}`} style={{ height }}>
      {track.kind === 'audio' ? <Music size={13} color="var(--ok)" /> : <Film size={13} color="var(--accent-2)" />}
      <span className="name" title="Doble clic para renombrar" onDoubleClick={() => {
        const n = window.prompt('Nombre de la pista', track.name)
        if (n && n.trim()) s().mutate((p) => { const t = p.tracks.find((x) => x.id === track.id); if (t) t.name = n.trim() })
      }}>{track.name}</span>
      {track.kind === 'video' && (
        <button className={`btn ghost icon sm ${!track.visible ? 'keep' : ''}`} title={track.visible ? 'Ocultar' : 'Mostrar'} onClick={() => toggle('visible')}>
          {track.visible ? <Eye size={13} /> : <EyeOff size={13} color="var(--danger)" />}
        </button>
      )}
      <button className={`btn ghost icon sm ${track.muted ? 'keep' : ''}`} title={track.muted ? 'Activar sonido' : 'Silenciar pista'} onClick={() => toggle('muted')}>
        {track.muted ? <VolumeX size={13} color="var(--danger)" /> : <Volume2 size={13} />}
      </button>
      <button className={`btn ghost icon sm ${track.locked ? 'keep' : ''}`} title={track.locked ? 'Desbloquear' : 'Bloquear'} onClick={() => toggle('locked')}>
        {track.locked ? <Lock size={13} color="var(--accent-2)" /> : <Unlock size={13} />}
      </button>
      <button className="btn ghost icon sm" title="Eliminar pista" onClick={() => {
        const has = s().project.clips.some((c) => c.trackId === track.id)
        if (!has || window.confirm(`¿Eliminar la pista «${track.name}» y sus clips?`)) s().removeTrack(track.id)
      }}><X size={13} /></button>
    </div>
  )
}

function niceStep(pps: number): { major: number; minor: number } {
  const steps = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600]
  const major = steps.find((s) => s * pps >= 90) ?? 600
  const minor = major / (major >= 10 ? 5 : major >= 1 ? 4 : 5)
  return { major, minor }
}

function fmtRuler(t: number, major: number): string {
  const m = Math.floor(t / 60)
  const s = t - m * 60
  if (major < 1) return `${m}:${s.toFixed(major < 0.5 ? 2 : 1).padStart(major < 0.5 ? 5 : 4, '0')}`
  return `${m}:${String(Math.round(s)).padStart(2, '0')}`
}

const Ruler = memo(function Ruler({ width, pps, onPointerDown }: { width: number; pps: number; onPointerDown: (e: React.PointerEvent) => void }) {
  const { major, minor } = niceStep(pps)
  const ticks: JSX.Element[] = []
  const total = width / pps
  const nMinor = Math.ceil(total / minor)
  for (let i = 0; i <= nMinor && i < 5000; i++) {
    const t = i * minor
    const isMajor = Math.abs(t / major - Math.round(t / major)) < 1e-6
    ticks.push(<div key={i} className="tick" style={{ left: t * pps, height: isMajor ? 10 : 5 }} />)
    if (isMajor) ticks.push(<div key={`l${i}`} className="lbl" style={{ left: t * pps }}>{fmtRuler(t, major)}</div>)
  }
  return <div className="ruler" style={{ width }} onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e) }}>{ticks}</div>
})

function Playhead({ height, onKnobDown }: { height: number; onKnobDown: (e: React.PointerEvent) => void }) {
  const x = useEditor((s) => s.playhead * s.pps)
  return (
    <div className="playhead" style={{ left: x, height }}>
      <div className="knob" onPointerDown={(e) => { e.stopPropagation(); onKnobDown(e) }} />
    </div>
  )
}

// ---------------- clip rendering ----------------

interface ClipProps {
  clip: Clip
  pps: number
  height: number
  selected: boolean
  locked: boolean
  asset?: Asset
  onDown: (e: React.PointerEvent, c: Clip) => void
  onTrim: (e: React.PointerEvent, c: Clip, side: 'l' | 'r') => void
}

const ClipView = memo(function ClipView({ clip, pps, height, selected, locked, asset, onDown, onTrim }: ClipProps) {
  const left = clip.startSec * pps
  const width = Math.max(4, clip.durationSec * pps)
  const label =
    clip.kind === 'text' ? clip.text?.text :
    clip.kind === 'subtitle' ? clip.rawText :
    asset?.name ?? 'Archivo no disponible'
  const inner = height - 8
  return (
    <div className={`clip ${clip.kind} ${selected ? 'sel' : ''} ${locked ? 'locked' : ''}`}
      style={{ left, width }} onPointerDown={(e) => onDown(e, clip)} title={label ?? ''}>
      {clip.kind === 'video' && asset && <Thumbs clip={clip} asset={asset} width={width} height={inner} pps={pps} />}
      {clip.kind === 'image' && asset && (
        <div className="strip" style={{ backgroundImage: `url(/api/media/${asset.id}/thumbnail?time=0&width=160)`, backgroundSize: `auto ${inner}px` }} />
      )}
      {(clip.kind === 'audio' || (clip.kind === 'video' && asset?.info.audioCodec && !clip.audio.muted)) && asset && (
        <Wave clip={clip} asset={asset} width={width} height={clip.kind === 'audio' ? inner - 14 : Math.min(18, inner / 3)} />
      )}
      <span className="label">{label}</span>
      {clip.speed !== 1 && <span className="badge">{clip.speed}x</span>}
      {clip.audio.fadeInSec > 0.05 && <div className="fade-mark" style={{ left: 0, width: clip.audio.fadeInSec * pps }} />}
      {clip.audio.fadeOutSec > 0.05 && <div className="fade-mark" style={{ right: 0, width: clip.audio.fadeOutSec * pps, transform: 'scaleX(-1)' }} />}
      {!locked && <div className="trim l" onPointerDown={(e) => onTrim(e, clip, 'l')} />}
      {!locked && <div className="trim r" onPointerDown={(e) => onTrim(e, clip, 'r')} />}
    </div>
  )
})

const STRIP_BUCKETS = [4, 8, 16, 32, 64, 128]

function Thumbs({ clip, asset, width, height, pps }: { clip: Clip; asset: Asset; width: number; height: number; pps: number }) {
  const ar = asset.info.width && asset.info.height ? asset.info.width / asset.info.height : 16 / 9
  const tileW = Math.max(24, height * ar)
  const assetPx = (asset.info.durationSec / clip.speed) * pps
  const needed = Math.ceil(assetPx / tileW)
  const count = STRIP_BUCKETS.find((b) => b >= needed) ?? 128
  const url = mediaStripUrl(asset.id, count, 96)
  const tiles: JSX.Element[] = []
  const n = Math.min(400, Math.ceil(width / tileW))
  for (let i = 0; i < n; i++) {
    const tLocal = ((i + 0.5) * tileW) / pps // seconds into clip (timeline)
    const src = clip.sourceInSec + tLocal * clip.speed
    const fi = Math.max(0, Math.min(count - 1, Math.floor((src / asset.info.durationSec) * count)))
    tiles.push(
      <div key={i} style={{
        position: 'absolute', left: i * tileW, top: 0, width: tileW, height,
        backgroundImage: `url(${url})`, backgroundSize: `${count * tileW}px ${height}px`, backgroundPosition: `${-fi * tileW}px 0`,
      }} />,
    )
  }
  return <div className="strip" style={{ overflow: 'hidden' }}>{tiles}</div>
}

function Wave({ clip, asset, width, height }: { clip: Clip; asset: Asset; width: number; height: number }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const [data, setData] = useState<Int16Array | null>(null)
  const peaks = Math.min(20000, Math.max(200, Math.ceil(asset.info.durationSec * 40)))
  useEffect(() => {
    let alive = true
    getWaveform(asset.id, peaks).then((d) => { if (alive) setData(d) }).catch(() => {})
    return () => { alive = false }
  }, [asset.id, peaks])
  const cw = Math.min(Math.ceil(width), 6000)
  useEffect(() => {
    const cv = ref.current
    if (!cv || !data || !data.length) return
    cv.width = cw
    cv.height = Math.max(4, Math.round(height))
    const ctx = cv.getContext('2d')!
    ctx.clearRect(0, 0, cv.width, cv.height)
    ctx.fillStyle = clip.kind === 'audio' ? 'rgba(170,255,210,.85)' : 'rgba(255,255,255,.55)'
    const perSec = data.length / asset.info.durationSec
    const s0 = clip.sourceInSec * perSec
    const s1 = clip.sourceOutSec * perSec
    const span = Math.max(1, s1 - s0)
    const gain = clip.audio.muted ? 0.2 : Math.min(2, clip.audio.volume)
    for (let x = 0; x < cw; x++) {
      const a = Math.floor(s0 + (x / cw) * span)
      const b = Math.max(a + 1, Math.floor(s0 + ((x + 1) / cw) * span))
      let m = 0
      for (let i = a; i < b && i < data.length; i++) { const v = Math.abs(data[i]); if (v > m) m = v }
      const h = Math.min(1, (m / 32768) * gain) * cv.height
      ctx.fillRect(x, cv.height - h, 1, h)
    }
  }, [data, cw, height, clip.sourceInSec, clip.sourceOutSec, clip.audio.volume, clip.audio.muted, clip.kind, asset.info.durationSec])
  return <canvas ref={ref} className="wave" style={{ width: '100%', height }} />
}
